// @flow

import { combineReducers } from 'redux';

import * as types from '../types/students';
import * as routeTypes from '../types/routes';
import * as common from './common';
import type { STUDENT_TYPE } from '../types/student';
import type { ERROR_TYPE } from '../types/common';

export type StudentsState = {
  byId: {[number]: STUDENT_TYPE},
  order: Array<number>,
  fetching: Array<number>,
  parents: Array<number>,
  fetchingGateways: Array<number>,
  selected: number,
  isFetching: boolean,
  isFetchingParents: boolean,
  isSavingRouteStopsSchedule: boolean,
  error: ERROR_TYPE
};

const byId = common.byId({
  added: [types.STUDENT_FETCH_SUCCEED],
  fetched: [
    types.STUDENTS_FETCH_SUCCEED,
    routeTypes.ROUTE_STOP_STUDENTS_FETCH_SUCCEED,
  ],
  updated: [
    types.STUDENT_BOARDED,
    types.FETCH_ASSIGNED_STOPS_SUCCESS,
    types.FETCH_STUDENT_GATEWAYS_SUCCESS,
    types.ADD_STUDENT_CHANGE_STOP_REQUESTS,
    types.REMOVE_STUDENT_PARENT_SUCCESS,
    types.ADD_STUDENT_PARENT_SUCCESS,
    types.STUDENT_UNMARKED,
    types.UPDATE_STUDENT_SUCCEED,
  ],
  defaultAttributes: {
    is_present: true,
  },
});

const order = common.order({
  added: [types.STUDENT_FETCH_SUCCEED],
  fetched: [types.STUDENTS_FETCH_SUCCEED],
});

const fetching = common.fetching({
  started: [
    types.STUDENT_FETCH_STARTED,
    types.STUDENT_ROUTES_FETCH_STARTED,
    types.FETCH_ASSIGNED_STOPS_STARTED,
  ],
  succeed: [
    types.STUDENT_FETCH_SUCCEED,
    types.STUDENT_ROUTES_FETCH_SUCCEED,
    types.FETCH_ASSIGNED_STOPS_SUCCESS,
  ],
  failed: [
    types.STUDENT_FETCH_FAILED,
    types.STUDENT_ROUTES_FETCH_FAILED,
  ],
});

const fetchingGateways = common.fetching({
  started: [
    types.FETCH_STUDENT_GATEWAYS_STARTED,
  ],
  succeed: [
    types.FETCH_STUDENT_GATEWAYS_SUCCESS,
  ],
  failed: [
    types.FETCH_STUDENT_GATEWAYS_FAILED,
  ],
});

const isFetching = common.isFetching({
  started: [types.STUDENTS_FETCH_STARTED],
  succeed: [types.STUDENTS_FETCH_SUCCEED],
  failed: [types.STUDENTS_FETCH_FAILED],
});

const isFetchingParents = common.isFetching({
  started: [types.FETCH_STUDENT_PARENTS_STARTED],
  succeed: [types.FETCH_STUDENT_PARENTS_SUCCEED],
  failed: [types.FETCH_STUDENT_PARENTS_FAILED],
});

const selected = common.mux({
  selected: [types.STUDENT_SELECTED],
  allDeselected: [types.STUDENT_SELECTION_CLEARED],
  default: -1,
});

const isSavingRouteStopsSchedule = common.isFetching({
  started: [types.SAVE_STOP_ASSIGNATIONS_STARTED],
  succeed: [types.SAVE_STOP_ASSIGNATIONS_SUCCEED],
  failed: [types.SAVE_STOP_ASSIGNATIONS_FAILED],
});

const error = common.error({
  populate: [types.STUDENTS_FETCH_FAILED],
  clear: [types.STUDENTS_FETCH_STARTED, types.STUDENTS_FETCH_SUCCEED],
});

const students = combineReducers({
  byId,
  order,
  isFetching,
  isFetchingParents,
  fetching,
  fetchingGateways,
  selected,
  isSavingRouteStopsSchedule,
  error,
});

export default students;

export const getStudent = (state: StudentsState, id: number): STUDENT_TYPE => state.byId[id];
export const getStudentRoutesStops = (state: StudentsState, id: number): ?Array<number> => {
  const student = state.byId[id];
  if (student) {
    return student.route_stops;
  }

  return null;
};
export const getStudentOrder = (state: StudentsState): Array<number> => state.order;
export const getStudents = (
  state: StudentsState,
): Array<STUDENT_TYPE> => state.order.map(id => getStudent(state, id));
export const isFetchingStudents = (state: StudentsState): boolean => state.isFetching;
export const isFetchingStudentParents = (state: StudentsState): boolean => state.isFetchingParents;
export const isStudentFetching = (
  state: StudentsState,
  id: number,
): boolean => state.fetching.includes(id);
export const isStudentFetchingGateways = (
  state: StudentsState,
  id: number,
): boolean => state.fetchingGateways.includes(id);
export const getSelectedStudent = (
  state: StudentsState,
): STUDENT_TYPE => state.byId[state.selected];
export const isSavingRouteStopAssignations = (
  state: StudentsState,
) => state.isSavingRouteStopsSchedule;
export const getStudentsError = (state: StudentsState): ERROR_TYPE => state.error;



// WEBPACK FOOTER //
// ./src/scripts/reducers/students.js